import React, { useState, useEffect, useCallback } from 'react';
import { Card, Table, Button, ToastContainer } from './ui';
import {
  AcademicCapIcon,
  BanknotesIcon,
  TrophyIcon,
  BuildingOfficeIcon,
  DocumentArrowDownIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import styles from './Reports.module.css';
import { safeErrorMessage } from '../utils/apiError';

const reportTypes = [
  { value: 'academic', label: 'Academic Report (Student)', icon: AcademicCapIcon },
  { value: 'financial', label: 'Financial Report', icon: BanknotesIcon },
  { value: 'sports', label: 'Sports Participation', icon: TrophyIcon },
  { value: 'ministry', label: 'Ministry Compliance', icon: BuildingOfficeIcon },
];

function Reports({ token }) {
  const [students, setStudents] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState('');
  const [academicReport, setAcademicReport] = useState(null);
  const [financialReport, setFinancialReport] = useState(null);
  const [sportsReport, setSportsReport] = useState(null);
  const [ministryReport, setMinistryReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [reportType, setReportType] = useState('academic');
  const [error, setError] = useState('');
  const [toasts, setToasts] = useState([]);
  const [dateRange, setDateRange] = useState({
    start_date: '',
    end_date: ''
  });

  const addToast = (message, type = 'success') => {
    setToasts(prev => [...prev, { message, type }]);
  };

  const removeToast = (index) => {
    setToasts(prev => prev.filter((_, i) => i !== index));
  };

  const fetchStudents = useCallback(async () => {
    if (!token) return;

    setLoading(true);
    setError('');

    try {
      const response = await fetch('/api/v1/students', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();

      if (Array.isArray(data)) {
        setStudents(data);
      } else if (data.students) {
        setStudents(data.students);
      } else {
        setStudents([]);
      }
    } catch (error) {
      console.error('Error fetching students:', error);
      setError(`Failed to load students: ${error.message}`);
      setStudents([]);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      fetchStudents();
    } else {
      setError('Authentication token not found. Please log in again.');
    }
  }, [token, fetchStudents]);

  const generateReport = async () => {
    if (!token) {
      setError('Authentication token not found');
      return;
    }

    if (reportType === 'academic' && !selectedStudent) {
      setError('Please select a student');
      return;
    }

    setLoading(true);
    setError('');

    try {
      let url = '';
      let response;

      switch (reportType) {
        case 'academic':
          url = `/api/v1/reports/academic/${selectedStudent}`;
          if (dateRange.start_date) {
            url += `?start_date=${dateRange.start_date}&end_date=${dateRange.end_date}`;
          }
          response = await fetch(url, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });
          if (response.ok) {
            const data = await response.json();
            setAcademicReport(data);
            setFinancialReport(null);
            setSportsReport(null);
            setMinistryReport(null);
          } else {
            throw new Error(await safeErrorMessage(response, 'Failed to generate academic report'));
          }
          break;

        case 'financial':
          url = '/api/v1/reports/financial';
          if (dateRange.start_date) {
            url += `?start_date=${dateRange.start_date}&end_date=${dateRange.end_date}`;
          }
          response = await fetch(url, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });
          if (response.ok) {
            const data = await response.json();
            setFinancialReport(data);
            setAcademicReport(null);
            setSportsReport(null);
            setMinistryReport(null);
          } else {
            throw new Error(await safeErrorMessage(response, 'Failed to generate financial report'));
          }
          break;

        case 'sports':
          response = await fetch('/api/v1/reports/sports-participation', {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });
          if (response.ok) {
            const data = await response.json();
            setSportsReport(data);
            setAcademicReport(null);
            setFinancialReport(null);
            setMinistryReport(null);
          } else {
            throw new Error(await safeErrorMessage(response, 'Failed to generate sports report'));
          }
          break;

        case 'ministry':
          const year = dateRange.start_date ? new Date(dateRange.start_date).getFullYear() : new Date().getFullYear();
          response = await fetch(`/api/v1/reports/ministry-compliance?year=${year}`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });
          if (response.ok) {
            const data = await response.json();
            setMinistryReport(data);
            setAcademicReport(null);
            setFinancialReport(null);
            setSportsReport(null);
          } else {
            throw new Error(await safeErrorMessage(response, 'Failed to generate ministry report'));
          }
          break;

        default:
          throw new Error('Invalid report type');
      }
    } catch (error) {
      console.error('Error generating report:', error);
      setError(error.message || 'Error generating report');
    } finally {
      setLoading(false);
    }
  };

  const exportReport = async (type) => {
    if (!token) {
      setError('Authentication token not found');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`/api/v1/reports/export/${type}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(await safeErrorMessage(response, 'Export failed'));
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${type}_report_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      addToast('Report exported successfully!');
    } catch (error) {
      console.error('Error exporting report:', error);
      setError(error.message || 'Error exporting report');
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (value, decimals = 2) => {
    if (value === undefined || value === null) return '0.00';
    const num = typeof value === 'number' ? value : parseFloat(value);
    return isNaN(num) ? '0.00' : num.toFixed(decimals);
  };

  const resultColumns = [
    { header: 'Subject', accessor: 'subject' },
    { header: 'Score', render: (row) => `${row.score}%` },
    { header: 'Grade', render: (row) => <strong>{row.zimsec_grade}</strong> },
    { header: 'Points', accessor: 'points' },
  ];

  let breakdownData = [];
  if (financialReport?.breakdown_by_method) {
    breakdownData = Object.entries(financialReport.breakdown_by_method).map(([method, amount]) => ({
      method: method.toUpperCase(),
      amount,
    }));
  }

  const breakdownColumns = [
    { header: 'Method', accessor: 'method' },
    { header: 'Total (USD)', render: (row) => `$${formatNumber(row.amount)}` },
  ];

  const sportColumns = [
    { header: 'Sport', accessor: 'sport' },
    { header: 'Participants', accessor: 'participants' },
  ];

  const subjectColumns = [
    { header: 'Subject', accessor: 'subject' },
    { header: 'Average Score', render: (row) => `${row.average_score}%` },
    { header: 'Total Exams', accessor: 'total_exams' },
  ];

  return (
    <div>
      <h2 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1.5rem' }}>
        <BuildingOfficeIcon style={{ width: 28, height: 28, color: 'var(--color-primary)' }} />
        Reports & Analytics
      </h2>

      {error && (
        <div className="alert alert-error" style={{ marginBottom: '1rem' }}>
          {error}
        </div>
      )}

      <Card
        title={
          <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <ArrowPathIcon style={{ width: 20, height: 20 }} />
            Generate Report
          </span>
        }
      >
        <div className={styles.filters}>
          <div className={styles.formGroup}>
            <label>Report Type</label>
            <select
              value={reportType}
              onChange={(e) => {
                setReportType(e.target.value);
                setAcademicReport(null);
                setFinancialReport(null);
                setSportsReport(null);
                setMinistryReport(null);
                setError('');
              }}
            >
              {reportTypes.map(t => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>

          {reportType === 'academic' && (
            <div className={styles.formGroup}>
              <label>Select Student *</label>
              <select
                value={selectedStudent}
                onChange={(e) => setSelectedStudent(e.target.value)}
                disabled={loading || students.length === 0}
              >
                <option value="">{students.length === 0 ? 'Loading students...' : 'Choose Student'}</option>
                {students.map(s => (
                  <option key={s.id} value={s.id}>
                    {s.full_name} {s.class_name ? `- ${s.class_name}` : ''}
                  </option>
                ))}
              </select>
            </div>
          )}

          {(reportType === 'academic' || reportType === 'financial') && (
            <>
              <div className={styles.formGroup}>
                <label>Start Date (Optional)</label>
                <input
                  type="date"
                  value={dateRange.start_date}
                  onChange={(e) => setDateRange({ ...dateRange, start_date: e.target.value })}
                />
              </div>
              <div className={styles.formGroup}>
                <label>End Date (Optional)</label>
                <input
                  type="date"
                  value={dateRange.end_date}
                  onChange={(e) => setDateRange({ ...dateRange, end_date: e.target.value })}
                />
              </div>
            </>
          )}
        </div>

        <div className={styles.actions}>
          <Button
            variant="primary"
            onClick={generateReport}
            disabled={loading || (reportType === 'academic' && !selectedStudent)}
            loading={loading}
          >
            {loading ? 'Generating...' : 'Generate Report'}
          </Button>

          {reportType !== 'ministry' && reportType !== 'sports' && (
            <Button
              variant="secondary"
              onClick={() => exportReport(reportType === 'academic' ? 'students' : 'fees')}
              disabled={loading}
            >
              <DocumentArrowDownIcon style={{ width: 16, height: 16 }} />
              Export CSV
            </Button>
          )}
        </div>
      </Card>

      {academicReport && academicReport.results && (
        <Card
          title={
            <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <AcademicCapIcon style={{ width: 20, height: 20 }} />
              Academic Report
            </span>
          }
          style={{ marginTop: '1.5rem' }}
        >
          <div className={styles.reportHeader}>
            <p><strong>Student ID:</strong> {academicReport.student_id}</p>
            <p><strong>Generated:</strong> {new Date(academicReport.generated_at).toLocaleString()}</p>
          </div>

          <div className={styles.statsGrid}>
            <div className={styles.statCard}>
              <div className={styles.statNumber}>{academicReport.academic?.subjects || 0}</div>
              <div className={styles.statLabel}>Subjects Taken</div>
            </div>
            <div className={styles.statCard}>
              <div className={styles.statNumber}>{academicReport.academic?.total_points || 0}</div>
              <div className={styles.statLabel}>Total Points</div>
            </div>
            <div className={styles.statCard}>
              <div className={styles.statNumber}>{formatNumber(academicReport.academic?.average_score)}%</div>
              <div className={styles.statLabel}>Average Score</div>
            </div>
            <div className={styles.statCard}>
              <div className={styles.statNumber}>{formatNumber(academicReport.attendance?.attendance_rate)}%</div>
              <div className={styles.statLabel}>Attendance Rate</div>
            </div>
          </div>

          <h4 style={{ marginBottom: '0.75rem' }}>Exam Results</h4>
          <Table columns={resultColumns} data={academicReport.results} />
        </Card>
      )}

      {financialReport && (
        <Card
          title={
            <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <BanknotesIcon style={{ width: 20, height: 20 }} />
              Financial Report
            </span>
          }
          style={{ marginTop: '1.5rem' }}
        >
          <div className={styles.statsGrid}>
            <div className={styles.statCard}>
              <div className={styles.statNumber}>${formatNumber(financialReport.total_collections_usd)}</div>
              <div className={styles.statLabel}>Total Collections (USD)</div>
            </div>
            <div className={styles.statCard}>
              <div className={styles.statNumber}>ZWG {formatNumber(financialReport.total_collections_zwg)}</div>
              <div className={styles.statLabel}>Total Collections (ZWG)</div>
            </div>
            <div className={styles.statCard}>
              <div className={styles.statNumber}>{financialReport.total_transactions || 0}</div>
              <div className={styles.statLabel}>Total Transactions</div>
            </div>
          </div>

          {breakdownData.length > 0 && (
            <>
              <h4 style={{ marginBottom: '0.75rem' }}>Breakdown by Payment Method</h4>
              <Table columns={breakdownColumns} data={breakdownData} />
            </>
          )}
        </Card>
      )}

      {sportsReport && (
        <Card
          title={
            <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <TrophyIcon style={{ width: 20, height: 20 }} />
              Sports Participation Report
            </span>
          }
          style={{ marginTop: '1.5rem' }}
        >
          {sportsReport.length === 0 ? (
            <p style={{ color: 'var(--color-text-muted)' }}>No sports participation data available.</p>
          ) : (
            <Table columns={sportColumns} data={sportsReport} />
          )}
        </Card>
      )}

      {ministryReport && (
        <Card
          title={
            <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <BuildingOfficeIcon style={{ width: 20, height: 20 }} />
              Ministry Compliance Report
            </span>
          }
          style={{ marginTop: '1.5rem' }}
        >
          <div className={styles.reportHeader}>
            <p><strong>School:</strong> {ministryReport.school_name}</p>
            <p><strong>Reporting Year:</strong> {ministryReport.reporting_year}</p>
            <p><strong>Report Date:</strong> {new Date(ministryReport.report_date).toLocaleString()}</p>
          </div>

          <h4 style={{ marginBottom: '0.75rem' }}>Enrollment Statistics</h4>
          <div className={styles.statsGrid}>
            <div className={styles.statCard}>
              <div className={styles.statNumber}>{ministryReport.enrollment?.total_students || 0}</div>
              <div className={styles.statLabel}>Total Students</div>
            </div>
            <div className={styles.statCard}>
              <div className={styles.statNumber}>{ministryReport.staffing?.total_teachers || 0}</div>
              <div className={styles.statLabel}>Total Teachers</div>
            </div>
          </div>

          {ministryReport.enrollment?.by_gender && ministryReport.enrollment.by_gender.length > 0 && (
            <>
              <h5 style={{ marginTop: '1rem', marginBottom: '0.5rem' }}>Students by Gender</h5>
              <ul className={styles.genderList}>
                {ministryReport.enrollment.by_gender.map((g, idx) => (
                  <li key={idx}>{g.gender}: {g.count} students</li>
                ))}
              </ul>
            </>
          )}

          {ministryReport.academic_performance && ministryReport.academic_performance.length > 0 && (
            <>
              <h5 style={{ marginTop: '1rem', marginBottom: '0.5rem' }}>Academic Performance</h5>
              <Table columns={subjectColumns} data={ministryReport.academic_performance} />
            </>
          )}

          <div className={styles.complianceBanner}>
            Compliance Status: {ministryReport.compliance_status || 'Compliant'}
          </div>
        </Card>
      )}

      <ToastContainer toasts={toasts} onClose={removeToast} />
    </div>
  );
}

export default Reports;
